import { LayoutSizeContext } from 'App';
import ConditionIcons from 'features/Weather/components/ConditionIcons/ConditionIcons';
import useLocation from 'hooks/useLocation';
import { useContext, useState } from 'react';
import { useGet15DayForecastQuery } from 'services/weather/weather.api';
import { ErrorMessage } from '../../../../../../components/Widgets/ErrorMessage';
import WidgetLoadingAnimation from '../../../../../../components/Widgets/WidgetLoadingAnimation';
import WidgetTimeUpdate from '../../../../../../components/Widgets/WidgetTimeUpdate';
import WeatherWidgetDetails from '../../../../components/WeatherWidgetDetails/WeatherWidgetDetails';
import WeatherWidgetSelector from '../../../../components/WeatherWidgetSelector/WeatherWidgetSelector';
import { day15WeatherDetails } from '../../features/Day15Forecast/config/day15Forecast.config';
import { layoutConfig, route } from './config/Day15ForecastWidget.config';
import {
  formatTileLabel,
  getDay15ForecastTileAriaLabel,
  getDay15ForecastWidgetLayout,
} from './utils/Day15ForecastWidget.utils';

const DayForecast15Widget = ({ layout, dimensions, containerSizePx }) => {
  const layoutSize = useContext(LayoutSizeContext); // baseFontSize, scaleMult, widgetWidth
  const { locations } = useLocation();
  const effectiveLocation = locations.effectiveLocation;
  const {
    data: day15Forecast,
    isLoading: loadingDay15Forecast,
    isError: isErrorday15Forecast,
    error: errorDay15Forecast,
    refetch: refetchDay15Forecast,
  } = useGet15DayForecastQuery(effectiveLocation);
  const [selected, setSelected] = useState(0);
  const {
    arrowButtonWidth,
    arrowIconSize,
    compact1x1VisibleCount,
    compact1x2ColumnCount,
    rowTileIconSize,
    tileIconSize,
    tileTextStyle,
  } = getDay15ForecastWidgetLayout({ containerSizePx, dimensions });
  const config = layoutConfig[layout] ?? layoutConfig['1x1'];
  const visibleNumOfItems = config.visibleCount({
    compact1x1VisibleCount,
    compact1x2ColumnCount,
  });

  if (isErrorday15Forecast && !loadingDay15Forecast) {
    return (
      <ErrorMessage
        message={errorDay15Forecast?.message ?? 'Unable to load 15 day forecast.'}
        retryRoute={route}
        onRetry={() => refetchDay15Forecast()}
      />
    );
  }

  if (loadingDay15Forecast)
    return <WidgetLoadingAnimation widgetName={'15DayForecast'} dimensions={dimensions} />;

  if (day15Forecast && day15Forecast?.length === 0) {
    return (
      <section aria-label="15-day forecast widget">
        <WidgetTimeUpdate route={route} updateIntervalMinutes={60} />
        <p className="p-3 mb-0" role="status">
          No data found.
        </p>
      </section>
    );
  }

  return (
    <section aria-label="15-day forecast widget" className={config.wrapperClassName}>
      <WidgetTimeUpdate route={route} compact={config.compactTimeUpdate} />
      {day15Forecast?.length && (
        <div className={config.contentClassName} style={config.contentStyle}>
          <div
            style={{
              flex: `0 0 ${config.selectorHeight}`,
              height: config.selectorHeight,
              minHeight: 0,
            }}
          >
            <WeatherWidgetSelector
              items={day15Forecast}
              selectedIndex={selected}
              visibleNumOfItems={visibleNumOfItems}
              selectorMode={config.selectorMode}
              gridColumnCount={compact1x2ColumnCount}
              gap={config.selectorGap}
              arrowButtonWidth={arrowButtonWidth}
              arrowIconSize={arrowIconSize}
              tileStyle={config.selectorTileStyle}
              ariaLabel="15-day forecast dates"
              previousButtonLabel="Show previous 15-day forecast date"
              nextButtonLabel="Show next 15-day forecast date"
              onSelect={(index) => setSelected(index)}
              getTileProps={(item) => ({
                label: `${formatTileLabel(item)}`,
                value: (
                  <div>
                    <div>{`${parseFloat(item.high_temp).toFixed(0)}°F`}</div>
                    <div>{`${parseFloat(item.low_temp).toFixed(0)}°F`}</div>
                  </div>
                ),
                ariaLabel: getDay15ForecastTileAriaLabel(item),
                icon: (
                  <span
                    aria-hidden="true"
                    className="d-flex h-100 w-100 align-items-center justify-content-center"
                  >
                    <ConditionIcons conditionId={item.icon} />
                  </span>
                ),
                iconStyle: {
                  width: config.selectorMode === 'row' ? rowTileIconSize : tileIconSize,
                  height: config.selectorMode === 'row' ? rowTileIconSize : tileIconSize,
                },
                labelStyle: tileTextStyle,
                valueStyle: tileTextStyle,
              })}
            />
          </div>
          <WeatherWidgetDetails
            selectedItem={day15Forecast[selected]}
            details={day15WeatherDetails}
            ariaLabel={`15-day forecast details for ${formatTileLabel(day15Forecast[selected])}`}
            layoutGroups={config.detailGroups}
            containerClassName={config.detailsClassName}
            containerStyle={config.detailsStyle({ layoutSize })}
            iconSize={config.detailsIconSize}
          />
        </div>
      )}
    </section>
  );
};

export default DayForecast15Widget;
